import { CheckCircle2, XCircle, type LucideIcon } from "lucide-react";

import type { DealListItem, DealOutcomeKind } from "@/api/client";
import { Button } from "@/components/ui/button";
import { FeedCard, type FeedTone } from "@/components/ui/feed-card";
import { formatAud } from "@/lib/currency";
import { declinedOutcomeLeadIn } from "@/lib/deal-labels";

interface Props {
  deal: DealListItem;
  onDismiss: () => void;
  /** Opens the connected deal detail (modal/drawer) — the [View deal] CTA. */
  onViewDeal: () => void;
}

/** DD/MM/YYYY (Australian). */
function formatAuDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

type Tone = "approved" | "declined";

interface OutcomeChrome {
  /** Uppercase header tag. */
  tag: string;
  tone: Tone;
  /** Footer label, e.g. "Date approved" / "New expiry date". */
  footerLabel: string;
  /** Which date the footer shows (defaults to reviewed_at). */
  footerDate: (deal: DealListItem) => string | null;
  /** Whether the body includes the deal value (closed-won cards omit it). */
  showValue: boolean;
}

/** Per-outcome card chrome (CHP-304). Registration- and extension-approved are
 * distinct cards now the backend persists `extension_approved_at` (migration
 * 0029); an amended extension (reviewer changed the date) gets its own tag. */
const OUTCOME_CHROME: Record<DealOutcomeKind, OutcomeChrome> = {
  registration_approved: {
    tag: "Registration approved",
    tone: "approved",
    footerLabel: "Date approved",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: true,
  },
  // CHP-368: a CM/RA/RM registered this deal on the partner's behalf, so it
  // landed LIVE in their portfolio without going through the review queue.
  // CHP-397: designer copy for the header.
  registration_assigned: {
    tag: "New deal added to your pipeline",
    tone: "approved",
    footerLabel: "Date approved",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: true,
  },
  registration_declined: {
    tag: "Registration declined",
    tone: "declined",
    footerLabel: "Date declined",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: true,
  },
  extension_approved: {
    tag: "Extension approved",
    tone: "approved",
    footerLabel: "New expiry date",
    footerDate: (d) => d.target_close_date,
    showValue: true,
  },
  extension_declined: {
    tag: "Extension declined",
    tone: "declined",
    footerLabel: "Date declined",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: true,
  },
  closed_won_approved: {
    tag: "Closed-Won approved",
    tone: "approved",
    footerLabel: "Date approved",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: false,
  },
  closed_won_declined: {
    tag: "Closed-Won declined",
    tone: "declined",
    footerLabel: "Date declined",
    footerDate: (d) => d.reviewed_at ?? null,
    showValue: false,
  },
  extension_approved_amended: {
    tag: "Extension approved (amended)",
    tone: "approved",
    footerLabel: "New expiry date",
    footerDate: (d) => d.target_close_date,
    showValue: true,
  },
};

const TONE_ICON: Record<Tone, LucideIcon> = {
  approved: CheckCircle2,
  declined: XCircle,
};

/** Outcome tone → FeedCard lane: positive → success, decline → coral (action). */
const FEED_TONE: Record<Tone, FeedTone> = {
  approved: "success",
  declined: "coral",
};

/**
 * Outcome confirmation card for a recently-reviewed deal (CHP-304).
 *
 * Built on the shared `FeedCard` primitive (CHP-523): the lane (success for
 * approvals, coral for declines) carries the 4px edge + eyebrow tag; the body
 * is Client name — Deal ID — Value, then the reviewer reason (declines only)
 * and the footer date; a [View deal] CTA opens the connected deal detail. The
 * exact variant comes from the server-computed `last_outcome` discriminator.
 *
 * [X] stamps outcome_dismissed_at (non-destructive, idempotent).
 */
export function DealOutcomeCard({ deal, onDismiss, onViewDeal }: Props) {
  // Defensive: deriveOutcomeCards already filters null outcomes, so this
  // never renders without a kind — fall back to the generic tag if it does.
  const chrome = deal.last_outcome
    ? OUTCOME_CHROME[deal.last_outcome]
    : OUTCOME_CHROME.registration_approved;
  const isDeclined = chrome.tone === "declined";
  const Icon = TONE_ICON[chrome.tone];
  // Preserve the original null-token contract: a missing value hides the
  // " — $value" segment entirely (the lib formatter would render "—" instead).
  const value = deal.total_contract_value != null ? formatAud(deal.total_contract_value) : null;

  // Declined extensions surface the partner-facing reason; rejected wins use
  // the generic review_reason (the only LIVE transition that stamps it).
  const reason =
    deal.last_outcome === "extension_declined"
      ? deal.extension_denial_reason
      : isDeclined
        ? deal.review_reason
        : null;
  // CHP-777: standardise the decline copy to "<Outcome> declined. Reason: <text>"
  // by prefixing the reason line with the outcome lead-in for this card's kind.
  const declineLeadIn = declinedOutcomeLeadIn(deal.last_outcome ?? null);

  return (
    // CHP-346: the whole card is clickable to open the deal (parity with the
    // manager feed cards, CHP-371). The [X] dismiss and [View deal] button stop
    // propagation so they don't also trigger the card-level open.
    <FeedCard
      tone={FEED_TONE[chrome.tone]}
      icon={Icon}
      eyebrow={chrome.tag}
      title={
        <>
          {deal.client_name}
          {" — "}
          {deal.deal_id_display ?? "—"}
          {chrome.showValue && value ? ` — ${value}` : ""}
        </>
      }
      description={
        <>
          {/* CHP-346/CHP-777: "<Outcome> declined. Reason: <text>" — the
              outcome lead-in then the reviewer's reason, as one text node. */}
          {reason ? <p>{`${declineLeadIn ? `${declineLeadIn} ` : ""}Reason: ${reason}`}</p> : null}
          <p className="text-xs text-muted-foreground">
            {chrome.footerLabel}: {formatAuDate(chrome.footerDate(deal))}
          </p>
        </>
      }
      onClick={onViewDeal}
      // Concise accessible name so the card doesn't read its whole text content
      // (incl. the [View deal] label) to screen readers.
      ariaLabel={`Open ${deal.client_name} deal`}
      onDismiss={onDismiss}
      dismissLabel={`Dismiss notification for ${deal.client_name}`}
      rawTestId={`outcome-card-${deal.id}`}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onViewDeal();
          }}
        >
          View deal
        </Button>
      }
    />
  );
}
